/**
 * /admin/diag-whatsapp — Diagnóstico del webhook de WhatsApp.
 * Muestra los últimos hits al endpoint POST /api/whatsapp/webhook
 * (incluso si la firma falla) para depurar mensajes entrantes en producción.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, CheckCircle2, XCircle, ChevronDown, ChevronRight, Activity, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";

async function exportarLogJson() {
  // Iter 84.5 — descarga directa con auth.
  try {
    const token = localStorage.getItem("dnf_access_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook-log/export.json`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) {
      toast.error(`No se pudo exportar (${r.status})`);
      return;
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `wa_webhook_log_${ts}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    toast.success("Log descargado");
  } catch (err) {
    toast.error("Error exportando log");
  }
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
}

function rel(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

export default function AdminDiagWhatsApp() {
  const [items, setItems] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const [logRes, healthRes] = await Promise.all([
        api.get("/whatsapp/webhook-log?limit=100"),
        api.get("/whatsapp/health").catch(() => ({ data: null })),
      ]);
      setItems(logRes.data?.items || []);
      setHealth(healthRes.data);
    } catch (e) {
      toast.error("Error cargando log webhook");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000); // refresco cada 10s
    return () => clearInterval(t);
  }, []);

  const toggleExpand = (idx) => {
    const next = new Set(expanded);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    setExpanded(next);
  };

  const stats = {
    total: items.length,
    sig_ok: items.filter((i) => i.signature_ok).length,
    sig_fail: items.filter((i) => !i.signature_ok).length,
    messages: items.filter((i) => (i.kinds || []).some((k) => k.startsWith("message:"))).length,
    statuses: items.filter((i) => (i.kinds || []).some((k) => k.startsWith("status:"))).length,
  };

  return (
    <div className="space-y-6" data-testid="admin-diag-whatsapp">
      <PageHeader title="Diagnóstico WhatsApp" subtitle="Últimos eventos recibidos por el webhook (refresco cada 10s)"
        actions={
          <Button size="sm" variant="outline"
            onClick={exportarLogJson}
            data-testid="btn-exportar-log-whatsapp"
            className="border-zinc-700 text-zinc-200 hover:bg-zinc-900">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Exportar JSON
          </Button>
        } />

      {/* Health Card */}
      {health && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-semibold text-zinc-200">Estado de conexión con Meta</h3>
          </div>
          {health.ok ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs text-zinc-500">Número</div>
                <div className="text-zinc-200 font-mono">{health.phone_info?.display_phone_number}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Nombre verificado</div>
                <div className="text-zinc-200">{health.phone_info?.verified_name}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Calidad</div>
                <div className="text-zinc-200">{health.phone_info?.quality_rating || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">APP_SECRET</div>
                <div className={health.app_secret_configured ? "text-emerald-400" : "text-amber-400"}>
                  {health.app_secret_configured ? "configurado" : "FALTA"}
                </div>
              </div>
              <div className="md:col-span-4">
                <div className="text-xs text-zinc-500">URL del webhook configurada en Meta debe ser:</div>
                <div className="text-zinc-300 font-mono text-xs break-all">{health.webhook_url}</div>
              </div>
            </div>
          ) : (
            <div className="text-amber-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Error contactando con Meta: {JSON.stringify(health.error)}
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total hits" value={stats.total} />
        <StatCard label="Firma OK" value={stats.sig_ok} color="emerald" />
        <StatCard label="Firma FALLA" value={stats.sig_fail} color={stats.sig_fail > 0 ? "red" : "zinc"} />
        <StatCard label="Mensajes" value={stats.messages} color="blue" />
        <StatCard label="Statuses" value={stats.statuses} color="zinc" />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          onClick={async () => {
            try {
              const r = await api.post("/whatsapp/migrate-legacy");
              const d = r.data || {};
              toast.success(`Migrados: ${d.migrated} · Saltados: ${d.skipped} · Legados: ${d.legacy_count}`);
              load();
            } catch (e) {
              toast.error("Error en migración: " + (e?.response?.data?.detail || e.message));
            }
          }}
          size="sm"
          variant="outline"
          data-testid="diag-wa-migrate"
        >
          Migrar webhooks legados
        </Button>
        <Button onClick={load} disabled={loading} size="sm" variant="outline" data-testid="diag-wa-refresh">
          <RefreshCw className={"w-3.5 h-3.5 mr-2 " + (loading ? "animate-spin" : "")} /> Refrescar
        </Button>
      </div>

      {/* Empty state */}
      {items.length === 0 && !loading && (
        <div className="rounded-xl border border-amber-900/40 bg-amber-950/20 p-6 text-center">
          <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
          <h3 className="text-amber-200 font-semibold mb-1">No se ha recibido NINGÚN webhook</h3>
          <p className="text-amber-300/70 text-sm max-w-xl mx-auto">
            Meta no está enviando peticiones a tu servidor. Causas probables:
          </p>
          <ul className="text-amber-300/70 text-sm mt-3 text-left max-w-lg mx-auto list-disc list-inside space-y-1">
            <li>El <b>número de teléfono</b> no está suscrito al campo <code className="text-amber-100">messages</code> en Meta (WhatsApp Manager → Configuración → Webhooks).</li>
            <li>La URL del webhook a nivel de App no coincide con la mostrada arriba.</li>
            <li>La app de Meta no está en modo "Live"/"Activo".</li>
            <li>El número está en una WABA distinta a la suscrita.</li>
          </ul>
        </div>
      )}

      {/* Lista de hits */}
      <div className="space-y-2">
        {items.map((it, idx) => {
          const isOpen = expanded.has(idx);
          return (
            <div key={idx} className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
              <button
                onClick={() => toggleExpand(idx)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-900 text-left"
                data-testid={`webhook-hit-${idx}`}
              >
                {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                {it.signature_ok ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-zinc-200 text-sm font-mono">{fmt(it.ts)}</span>
                    <span className="text-zinc-500 text-xs">{rel(it.ts)}</span>
                    {(it.kinds || []).map((k, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{k}</Badge>
                    ))}
                    {it.error && <Badge className="bg-red-900/40 text-red-200 text-xs">{it.error}</Badge>}
                  </div>
                </div>
                <span className="text-xs text-zinc-500">{it.size_bytes}B</span>
              </button>
              {isOpen && (
                <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-900/50">
                  <div className="text-xs text-zinc-500 mb-1">Payload (primeros 2KB):</div>
                  <pre className="text-xs text-zinc-300 overflow-x-auto whitespace-pre-wrap break-all max-h-96">
                    {it.preview || "(vacío)"}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, color = "zinc" }) {
  const colorMap = {
    emerald: "text-emerald-400",
    red: "text-red-400",
    blue: "text-blue-400",
    zinc: "text-zinc-200",
  };
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className={`text-2xl font-bold ${colorMap[color] || colorMap.zinc}`}>{value}</div>
    </div>
  );
}
